import { useMemo } from "react"
import styled from "styled-components"

import { UITypes } from "types"

import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ButtonNew } from "components/form/ButtonNew"
import { SelectStatic } from "components/form/SelectStatic"

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;
`

const MarkAllButton = styled(ButtonNew)`
  height: 32px;
  color: #334155 !important;
  border: 1px solid #e2e8f0 !important;
  flex-shrink: 0;
  margin-left: auto;

  &:hover {
    background-color: #f8fafc;
  }
`

const StyledSelectStatic = styled(SelectStatic)`
  width: auto;
  margin-bottom: 0;

  .static-select__control {
    min-height: 32px !important;
  }
`

const TIME_RANGE_OPTIONS = [
  { value: 0, label: "All time" },
  { value: 1, label: "Last 24 hours" },
  { value: 3, label: "Last 3 days" },
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
]

interface NotificationsFiltersProps {
  eventTypeOptions: UITypes.Option[]
  isMarkingAllAsRead: boolean
  isFetching: boolean
  onMarkAllAsRead: () => void
}

export const NotificationsFilters = ({
  eventTypeOptions,
  isMarkingAllAsRead,
  isFetching,
  onMarkAllAsRead,
}: NotificationsFiltersProps) => {
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()

  const selectedEventType = urlQueryParams["eventTypeId"] ? parseInt(urlQueryParams["eventTypeId"] as string) : null
  const selectedTimeRange = urlQueryParams["rangeDays"] ? parseInt(urlQueryParams["rangeDays"] as string) : 0

  const selectedEventTypeOption = useMemo(() => {
    const valueToFind = selectedEventType === null ? 0 : selectedEventType
    return eventTypeOptions.find(opt => opt.value === valueToFind)
  }, [eventTypeOptions, selectedEventType])

  const selectedTimeRangeOption = useMemo(() => {
    return TIME_RANGE_OPTIONS.find(opt => opt.value === selectedTimeRange)
  }, [selectedTimeRange])

  const handleEventTypeChange = (eventTypeId: number | null) => {
    setUrlQueryParams({
      ...urlQueryParams,
      eventTypeId: eventTypeId !== null ? eventTypeId.toString() : null,
      page: "1",
    })
  }

  const handleTimeRangeChange = (days: number) => {
    setUrlQueryParams({
      ...urlQueryParams,
      rangeDays: days > 0 ? days.toString() : null,
      page: "1",
    })
  }

  return (
    <FilterRow>
      <StyledSelectStatic
        options={eventTypeOptions}
        values={selectedEventTypeOption ? [selectedEventTypeOption] : []}
        onChange={options => {
          const option = options[0]
          handleEventTypeChange(option?.value === 0 ? null : (option?.value as number))
        }}
        placeholder="All notifications"
        minWidth="340px"
      />
      <StyledSelectStatic
        options={TIME_RANGE_OPTIONS}
        values={selectedTimeRangeOption ? [selectedTimeRangeOption] : []}
        onChange={options => {
          const option = options[0]
          handleTimeRangeChange((option?.value as number) || 0)
        }}
        placeholder="All time"
        minWidth="200px"
      />

      <MarkAllButton onClick={onMarkAllAsRead} disabled={isMarkingAllAsRead || isFetching} color="greyBordered">
        Mark All as Read
      </MarkAllButton>
    </FilterRow>
  )
}
